import i18n from "@/i18n";
/**
 * Prompt 12G-2 — Local Shift Cell Editor.
 *
 * Reusable Schedule-cell editor owning ONLY local interaction:
 * whether a cell is direct-editable, entering edit mode, the inline
 * input, initial text, parsing through Prompt-12G-1
 * `parseQuickShiftInput`, Enter / Tab / Shift+Tab commit intents,
 * Escape / blank cancel, invalid-input guidance, deferred blur
 * commit intent, and non-editing Delete / Backspace delete intent.
 *
 * Prompt 12G-2 §43–§47 — no auto-save UI state, no API calls, no
 * grid re-read, no revision/overlap/not-applicable handling, no
 * focus navigation. Those responsibilities land in Prompts 12G-3 /
 * 12G-4.
 * Prompt 12G-2 §48 — this component is intentionally NOT wired into
 * ScheduleGrid in this block; the live 21-day grid stays read-only.
 */
import React, {
    useEffect,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import {
    parseQuickShiftInput,
    normalizeShiftIntervalInput,
} from "@/components/workforce/workforceHelpers";

// Prompt 18B2-P4 §6 · §7 · §10 — thin wrapper: pre-normalize
// friendly manager syntax ("20.20-22.30", "2020-2230", etc.) to
// canonical "HH:MM-HH:MM" and defer to the existing strict Shift
// parser. When the shape is not a two-token interval, the raw
// string is passed through so `parseQuickShiftInput` remains the
// sole authority on validity. Existing 10-minute planning
// validation continues to run inside `parseQuickShiftInput`.
function _parseFriendlyShiftInput(raw, workDate) {
    const normalized = normalizeShiftIntervalInput(raw) || raw;
    return parseQuickShiftInput(normalized, workDate);
}

// Prompt 12G-2 §29 — exact manager-facing guidance for invalid quick
// input. Do NOT expose parser internals or minute-resolution rules.
export const SHIFT_CELL_INVALID_GUIDANCE =
    "Use a time like 8-16 or 8:30-16:30.";

// Prompt 12G-2 §9 — direct editing eligibility.
function _isEditable(cell) {
    if (!cell || cell.applicable !== true) return false;
    if (!Array.isArray(cell.shifts)) return false;
    return cell.shifts.length === 0 || cell.shifts.length === 1;
}

export function ShiftCellEditor({
    cell,
    children,
    onCommitIntent,
    onDeleteIntent,
    cellKey,
    cancelEditVersion,
}) {
    const editable = _isEditable(cell);
    const existingShift =
        editable && cell.shifts.length === 1 ? cell.shifts[0] : null;

    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState("");

    const inputRef = useRef(null);
    // Prompt 12G-2 §34 — cancellation token for the deferred blur
    // commit. Enter / Tab / Shift+Tab / Escape / unmount MUST cancel.
    const blurTimerRef = useRef(null);
    // Prompt 12G-2 §35 · §36 — Enter/Tab + blur MUST emit at most one
    // intent. This ref is flipped whenever a definitive commit (or
    // cancel) has occurred so a still-pending deferred blur no-ops.
    const committedRef = useRef(false);
    // Prompt 12G-4 §9 — initial mount MUST NOT trigger cancellation.
    const cancelSeenRef = useRef(cancelEditVersion);

    const cancelBlurTimer = () => {
        if (blurTimerRef.current !== null) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
    };

    // Prompt 12G-2 §34 · §78 — unmount cancels pending deferred blur.
    useEffect(() => {
        return () => {
            cancelBlurTimer();
        };
    }, []);

    // Prompt 12G-4 §8 · §10 — an opaque parent-controlled bump in
    // cancelEditVersion cancels any active edit and any pending
    // deferred blur commit, without emitting any intent.
    useEffect(() => {
        if (cancelSeenRef.current === cancelEditVersion) return;
        cancelSeenRef.current = cancelEditVersion;
        // Cancel deferred blur.
        cancelBlurTimer();
        committedRef.current = true;
        setIsEditing(false);
        setText("");
    }, [cancelEditVersion]);

    // Prompt 12G-2 §20 — focus input when edit mode opens; select
    // pre-filled single-shift text when practical.
    useEffect(() => {
        if (!isEditing) return;
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        if (existingShift) {
            try {
                el.select();
            } catch (_e) {
                /* jsdom fallback — normal text selection is optional. */
            }
        }
    // Intentional deps: re-focus each time we re-enter edit mode.
    // existingShift is captured via cell.shifts and won't change during
    // an edit session because commit exits editing before mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing]);

    // Prompt 12G-2 §21 — typing is purely local state.
    const handleTextChange = (e) => {
        setText(e.target.value);
    };

    // Prompt 12G-2 §12 · §13 — open editor with correct initial text.
    const openEditor = () => {
        committedRef.current = false;
        cancelBlurTimer();
        if (existingShift) {
            setText(`${existingShift.start_time}-${existingShift.end_time}`);
        } else {
            setText("");
        }
        setIsEditing(true);
    };

    // Prompt 12G-2 §6 — canonical commit payload emission.
    const emitCommit = (parsed, source) => {
        if (committedRef.current) return;
        committedRef.current = true;
        cancelBlurTimer();
        if (typeof onCommitIntent === "function") {
            onCommitIntent({
                mode: existingShift ? "update" : "create",
                parsed,
                shift: existingShift || null,
                source,
            });
        }
        setIsEditing(false);
        setText("");
    };

    // Prompt 12G-2 §7 · §38 — canonical delete payload emission.
    const emitDelete = () => {
        if (!existingShift) return;
        if (typeof onDeleteIntent === "function") {
            onDeleteIntent({ shift: existingShift });
        }
    };

    const showGuidance = () => {
        toast.error(i18n.t("wf.ShiftCellEditor.SHIFT_CELL_INVALID_GUIDANCE", SHIFT_CELL_INVALID_GUIDANCE));
    };

    // Prompt 12G-2 §17 · §37 · §38 — non-editing wrapper key handling.
    const handleWrapperKeyDown = (e) => {
        if (e.key === "Enter") {
            // Prompt 12G-2 §17 — Enter enters edit mode.
            e.preventDefault();
            openEditor();
            return;
        }
        if (e.key === "Delete" || e.key === "Backspace") {
            // Prompt 12G-2 §37 — empty cell: no-op but suppress
            // undesirable browser default navigation for Backspace.
            e.preventDefault();
            if (existingShift) {
                // Prompt 12G-2 §38 — single Shift: delete intent.
                emitDelete();
            }
        }
    };

    const handleWrapperClick = () => {
        // Prompt 12G-2 §18 — single click enters edit mode.
        openEditor();
    };

    // Prompt 12G-2 §22–§26 · §29–§31 — inline input key handling.
    const handleInputKeyDown = (e) => {
        if (e.key === "Escape") {
            // Prompt 12G-2 §25 · §34 — cancel, discard text, cancel
            // any pending deferred blur commit.
            e.preventDefault();
            cancelBlurTimer();
            committedRef.current = true;
            setIsEditing(false);
            setText("");
            return;
        }

        if (e.key === "Enter") {
            e.preventDefault();
            const raw = typeof text === "string" ? text : "";
            const trimmed = raw.trim();
            if (trimmed === "") {
                // Prompt 12G-2 §26 — blank Enter cancels silently.
                cancelBlurTimer();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parsed = _parseFriendlyShiftInput(raw, cell.date);
            if (parsed === null) {
                // Prompt 12G-2 §30 — invalid, keep editing.
                cancelBlurTimer();
                showGuidance();
                return;
            }
            emitCommit(parsed, "enter");
            return;
        }

        if (e.key === "Tab") {
            e.preventDefault();
            const source = e.shiftKey ? "shift-tab" : "tab";
            const raw = typeof text === "string" ? text : "";
            const trimmed = raw.trim();
            if (trimmed === "") {
                // Prompt 12G-2 §27 — blank Tab / Shift+Tab cancels.
                cancelBlurTimer();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parsed = _parseFriendlyShiftInput(raw, cell.date);
            if (parsed === null) {
                // Prompt 12G-2 §31 — invalid, keep editing.
                cancelBlurTimer();
                showGuidance();
                return;
            }
            emitCommit(parsed, source);
        }
    };

    // Prompt 12G-2 §33 — valid blur commit is deferred by one browser
    // task turn so a coincident Enter/Tab/Shift+Tab/Escape gets to
    // win first. Prompt 12G-2 §28 · §32 also route through this handler
    // with immediate exit semantics inside the deferred callback.
    const handleInputBlur = () => {
        cancelBlurTimer();
        const capturedText = text;
        const capturedDate = cell.date;
        blurTimerRef.current = setTimeout(() => {
            blurTimerRef.current = null;
            if (committedRef.current) return;
            const trimmed =
                typeof capturedText === "string" ? capturedText.trim() : "";
            if (trimmed === "") {
                // Prompt 12G-2 §28 — blank blur exits silently.
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            const parsed = _parseFriendlyShiftInput(
                capturedText, capturedDate);
            if (parsed === null) {
                // Prompt 12G-2 §32 — invalid blur → guidance + exit.
                showGuidance();
                committedRef.current = true;
                setIsEditing(false);
                setText("");
                return;
            }
            // Prompt 12G-2 §33 — valid deferred blur commit.
            emitCommit(parsed, "blur");
        }, 0);
    };

    // Prompt 12G-2 §10 · §11 — non-editable passthrough.
    if (!editable) {
        return <>{children}</>;
    }

    if (isEditing) {
        // Prompt 12G-2 §15 · §19 — inline input only; no read-only
        // display, no Save/Cancel buttons.
        return (
            <Input
                ref={inputRef}
                value={text}
                onChange={handleTextChange}
                onKeyDown={handleInputKeyDown}
                onBlur={handleInputBlur}
                data-testid="shift-cell-editor-input"
                aria-label={i18n.t("wf.shiftCellEditor.shiftQuickInput", "Shift quick input")}
            />
        );
    }

    // Prompt 12G-2 §15 · §16 — non-editing focusable wrapper renders
    // children exactly once and never uses a direct-edit DOM primitive.
    // Prompt 12G-4 §7 — expose the collision-safe logical cell key so
    // ScheduleGrid can perform DOM focus traversal.
    return (
        <span
            tabIndex={0}
            role="button"
            onClick={handleWrapperClick}
            onKeyDown={handleWrapperKeyDown}
            data-testid="shift-cell-editor-wrapper"
            data-workforce-shift-cell-key={cellKey}
        >
            {children}
        </span>
    );
}

export default ShiftCellEditor;
